import { useEffect, useMemo, useState } from 'react';
import PricingTables from './PricingTable';
import { useLocation, useNavigate } from 'react-router-dom';
import { userAppContext } from '../../context/Context';
import { useAcademicApplication } from '../../hooks/useAcademicApplication';
import { PricingCard } from './PricingCard';
import { UserContext } from '../../types';
import { PricingData } from './types';
import AcademicApplicationModal from '../Academic/AcademicApplicationModal';
import SharedButton from '@/global/Button/Button';
import BackgroundPages from '@/global/Background/BackgroundPages';
import TabSelector from '@/global/TabSelector/TabSelector';
import { usePricingStats } from './usePricingStats';
import {
  ChartBarIcon,
  CircleStackIcon,
  ComputerDesktopIcon,
  BeakerIcon,
  MagnifyingGlassIcon,
  WifiIcon,
} from '@heroicons/react/24/solid';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const defaultStatsLineAcademic = '14,100+ genes · 933,700+ variants · 14,800+ diseases';
const defaultStatsLineFull = '30,300+ genes · 1.6M+ variants · 38,100+ diseases';

const basePricings: PricingData[] = [
  {
    title: 'Academic',
    price: 'Free',
    included: 'Access our expert-curated dataset for academic research through the web platform.',
    details: [
      {
        label: 'Expert-curated knowledgebase',
        icon: <CircleStackIcon className="w-4 h-4" />,
      },
      {
        label: 'Web platform, REST API, R package & Cytoscape App access',
        icon: <ComputerDesktopIcon className="w-4 h-4" />,
      },
      {
        label: defaultStatsLineAcademic,
        icon: <BeakerIcon className="w-4 h-4" />,
      },
      {
        label: 'Enrichment analysis',
        icon: <ChartBarIcon className="w-4 h-4" />,
      },
      {
        label: 'Advanced Search',
        icon: <MagnifyingGlassIcon className="w-4 h-4" />,
      },
      // {
      //   label: 'Perfect for: Courses, initial projects, verification',
      //   icon: <CheckCircleIcon className="w-4 h-4" />,
      // },
    ],
  },
  {
    title: 'Standard',
    price: '1,199',
    priceMo: '99.92',
    included:
      'Full access to the complete dataset through the web platform, including all metrics and annotations.',
    details: [
      {
        label: 'Full dataset access',
        icon: <CircleStackIcon className="w-4 h-4" />,
      },
      {
        label: 'Web platform',
        icon: <ComputerDesktopIcon className="w-4 h-4" />,
      },
      {
        label: defaultStatsLineFull,
        icon: <BeakerIcon className="w-4 h-4" />,
      },
      {
        label: 'Enrichment analysis',
        icon: <ChartBarIcon className="w-4 h-4" />,
      },
      {
        label: 'Advanced search',
        icon: <MagnifyingGlassIcon className="w-4 h-4" />,
      },
      {
        label: 'AIDA',
        icon: <img src="../../../static/images/eyesAida.svg" alt="Web platform" className="w-4 h-4" />,
      },
      // {
      //   label: 'Perfect for: Active researchers, labs, startups',
      //   icon: <CheckCircleIcon className="w-4 h-4" />,
      // },
    ],
  },
  {
    info: '✳ Most Popular ✳',
    title: 'Advanced',
    price: '1,999',
    priceMo: '166.58',
    included:
      'Everything in Standard, plus full REST API access to integrate DISGENET into your tools and workflows.',
    details: [
      {
        label: 'Full dataset access',
        icon: <CircleStackIcon className="w-4 h-4" />,
      },
      {
        label: 'Web platform, REST API, R package & Cytoscape App access',
        icon: <ComputerDesktopIcon className="w-4 h-4" />,
      },
      {
        label: defaultStatsLineFull,
        icon: <BeakerIcon className="w-4 h-4" />,
      },
      {
        label: 'Enrichment analysis',
        icon: <ChartBarIcon className="w-4 h-4" />,
      },
      {
        label: 'Advanced search',
        icon: <MagnifyingGlassIcon className="w-4 h-4" />,
      },
      {
        label: 'AIDA',
        icon: <img src="../../../static/images/eyesAida.svg" alt="Web platform" className="w-4 h-4" />,
      },
      // {
      //   label: 'Perfect for: Bioinformaticians, data scientists+',
      //   icon: <CheckCircleIcon className="w-4 h-4" />,
      // },
    ],
  },
  {
    title: 'Premium',
    included: 'Enterprise access with full dataset downloads and seamless integration into internal systems.',
    description: '**Ideal for**: large organizations with custom workflow needs.',
    details: [
      {
        label: 'Full dataset access',
        icon: <CircleStackIcon className="w-4 h-4" />,
      },
      {
        label: 'Web platform, REST API, R package & Cytoscape App access',
        icon: <ComputerDesktopIcon className="w-4 h-4" />,
      },
      {
        label: defaultStatsLineFull,
        icon: <BeakerIcon className="w-4 h-4" />,
      },
      {
        label: 'Enrichment analysis',
        icon: <ChartBarIcon className="w-4 h-4" />,
      },
      {
        label: 'Advanced search',
        icon: <MagnifyingGlassIcon className="w-4 h-4" />,
      },
      {
        label: 'Offline access',
        icon: <WifiIcon className="w-4 h-4" />,
      },
      {
        label: 'AIDA',
        icon: <img src="../../../static/images/eyesAida.svg" alt="Web platform" className="w-4 h-4" />,
      },
    ],
  },
];

function isStatsDetailLabel(label: string): boolean {
  return /genes.*variants.*diseases/i.test(label) || (label.includes('genes') && label.includes('variants'));
}

function RenderPricings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { statsLine } = usePricingStats();
  const [showModalAcademic, setShowModalAcademic] = useState(false);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const { user, isAuthenticated, groups } = userAppContext() as unknown as UserContext;
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') || 'academic';
  });
  const {
    submitAcademicApplication,
    academicStatus,
    trialType,
    loadingStatus,
    canSeePrices: canSeePricesFromHook,
    isCommercial: isCommercialFromHook,
  } = useAcademicApplication(user?.email, isAuthenticated);
  const isUserDataLoading = isAuthenticated && loadingStatus;

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isStandardPlan = groups?.includes('ROLE_STANDARD');
  const isAcademicPlan = groups?.includes('ROLE_ACADEMIC');

  useEffect(() => {
    const tab = location.search.split('tab=')[1];
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Use tag-based logic from hook
  const canSeePrices = canSeePricesFromHook();

  // Users without pricing access are treated as commercial
  const shouldShowContactUs =
    !isUserDataLoading && isAuthenticated && (isStandardPlan || isAcademicPlan) && !canSeePrices;

  const isCommercial = isTrial
    ? trialType === 'commercial'
    : !isUserDataLoading && (isCommercialFromHook() || shouldShowContactUs);

  // For authenticated users, lock the tab based on their type
  const effectiveTab = isAuthenticated ? (isCommercial ? 'business' : 'academic') : activeTab;

  const handleSeeMore = () => {
    setIsTableExpanded(true);
    setTimeout(() => {
      const tableSection = document.getElementById('pricingTableSection');
      tableSection?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const handleAcademicApply = () => {
    setShowModalAcademic(true);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/plans?tab=${value}`);
  };

  const pricings = useMemo(
    () =>
      basePricings.map(p => ({
        ...p,
        details: p.details?.map(d => (isStatsDetailLabel(d.label) ? { ...d, label: statsLine } : d)),
      })),
    [statsLine],
  );

  const options = [
    { label: 'Academic', value: 'academic' },
    { label: 'Business', value: 'business' },
  ];

  return (
    <>
      <BackgroundPages />
      <div className="PricingApp mt-12">
        <link rel="canonical" href="/plans" />
        <div className="app-container">
          <header>
            <h1 className="header-topic gradient-color" id="pricingSection">
              Our Plans
            </h1>
          </header>
          {!isAuthenticated && (
            <div className="mt-4">
              <TabSelector
                options={options}
                variant="primary"
                size="md"
                value={effectiveTab}
                onChange={handleTabChange}
                className="border"
              />
            </div>
          )}
          <div className="pricing-cards mt-8" style={{ alignItems: 'center' }}>
            {pricings
              .filter(pricing => {
                // Tab-based filtering: Academic tab hides Premium, Business tab hides Academic
                if (effectiveTab === 'academic' && pricing.title === 'Premium') return false;
                if (effectiveTab === 'business' && pricing.title === 'Academic') return false;

                if (!isAuthenticated) return true;

                // Hide Academic card for commercial users (including those without pricing access)
                if ((isCommercial || shouldShowContactUs) && pricing.title === 'Academic') return false;

                if (pricing.title === 'Premium' && isTrial && trialType === 'academic') return false;

                if (pricing.title === 'Premium' && !isCommercial && !isTrial && !shouldShowContactUs)
                  return false;

                return true;
              })
              .map(pricing => (
                <PricingCard
                  key={pricing.title}
                  {...pricing}
                  onAcademicApply={handleAcademicApply}
                  onSeeMore={handleSeeMore}
                  isCommercial={isCommercial}
                  isUserDataLoading={isUserDataLoading}
                  activeTab={effectiveTab}
                />
              ))}
          </div>
          <div className="div-whats-included mt-12">
            <div className="pt-4">
              <PricingTables
                activeTab={effectiveTab}
                isExpanded={isTableExpanded}
                onToggleExpand={() => setIsTableExpanded(prev => !prev)}
                onAcademicApply={handleAcademicApply}
              />
            </div>
          </div>
          {isCommercial && (
            <div className="flex justify-center pt-4 pb-8 w-full">
              <div className="w-3/4 md:w-1/4">
                <SharedButton
                  variant="primary"
                  size="md"
                  className="flex justify-center w-full"
                  onClick={() => navigate('/Support')}
                >
                  Contact Us
                </SharedButton>
              </div>
            </div>
          )}
        </div>

        {/* @ts-ignore */}
        <AcademicApplicationModal
          isOpen={showModalAcademic}
          onClose={() => setShowModalAcademic(false)}
          onSubmit={submitAcademicApplication}
          academicStatus={academicStatus}
        />
      </div>
    </>
  );
}

export default RenderPricings;
